import styled from 'styled-components';

import {
    GetManualSupplyOrdersQuery,
    GetManualSupplyOrderSupplierProductsStockVariationQuery,
} from 'data/__generated__';
import { ManualSupplyOrdersFormData } from 'pages/ManualSupplyOrders/ManualSupplyOrdersForm';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { SupplyOrderProductsRows } from './SupplyOrderProductsRows';

export const ProductsInfoTable = ({
    supplyOrders,
    stockVariationData,
    stockVariationLoading,
    stockVariationError,
    updateForecastsLoading,
}: {
    supplyOrders: GetManualSupplyOrdersQuery['manualSupplyOrders'];
    stockVariationData?: GetManualSupplyOrderSupplierProductsStockVariationQuery['manualSupplyOrderSupplierProductsStockVariation'];
    stockVariationLoading?: boolean;
    stockVariationError?: Error | null;
    updateForecastsLoading?: boolean;
}) => {
    // Extraire tous les fournisseurs uniques
    const allSupplyOrders = supplyOrders.map((supplyOrder) => {
        return {
            _id: supplyOrder._id,
            name: supplyOrder.supplier.name,
        };
    });

    const methods = useFormContext<ManualSupplyOrdersFormData>();
    const { fields: supplyOrdersFields } = useFieldArray({
        control: methods.control,
        name: 'supplyOrders',
    });

    return (
        <>
            <SupplierProductInfoTableContainer>
                <SupplierProductInfoTable>
                    <thead>
                        <SupplierProductInfoHeaderRow>
                            <ActionsInfoHeaderCellSmall />
                            <SupplierProductInfoHeaderCell>Fournisseur</SupplierProductInfoHeaderCell>
                            <SupplierProductInfoHeaderCell>Nom du produit</SupplierProductInfoHeaderCell>
                            <SupplierProductInfoHeaderCellSmall>Stock (disponible)</SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>
                                Colisage / Palettisation
                            </SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>
                                Nb. Palettes demandées
                            </SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>Nb. Colis demandés</SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>Quantité demandée</SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>
                                Nb. prédictions (base / extended)
                            </SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>Prix unitaire HT</SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCellSmall>Total HT à recevoir</SupplierProductInfoHeaderCellSmall>
                            <SupplierProductInfoHeaderCell>Variation Stock</SupplierProductInfoHeaderCell>
                        </SupplierProductInfoHeaderRow>
                    </thead>
                    <tbody>
                        {supplyOrdersFields.map((supplyOrderField, supplyOrderIndex) => {
                            // Trouver le supplyOrder correspondant dans les données originales
                            const originalSupplyOrder = supplyOrders.find((so) => so._id === supplyOrderField._id);

                            if (!originalSupplyOrder) return null;

                            // Utiliser useFieldArray pour les products de ce supplyOrder
                            return (
                                <SupplyOrderProductsRows
                                    key={supplyOrderField.id}
                                    supplyOrderIndex={supplyOrderIndex}
                                    originalSupplyOrder={originalSupplyOrder}
                                    allSupplyOrders={allSupplyOrders}
                                    stockVariationData={stockVariationData}
                                    stockVariationLoading={stockVariationLoading}
                                    stockVariationError={stockVariationError}
                                    updateForecastsLoading={updateForecastsLoading}
                                />
                            );
                        })}
                    </tbody>
                </SupplierProductInfoTable>
            </SupplierProductInfoTableContainer>
        </>
    );
};

const SupplierProductInfoTableContainer = styled.div`
    // we compute the height with vh because we want the column to be visible while taking the maximum height
    width: 100%;
    max-height: calc(100vh - 200px);
    overflow: scroll;
    margin-top: 20px;

    ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 7px;
        height: 7px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0.5);
        -webkit-box-shadow: 0 0 2px rgba(255, 255, 255, 0.5);
    }
`;

const SupplierProductInfoTable = styled.table`
    border-collapse: collapse;
`;

// Table Header
const SupplierProductInfoHeaderRow = styled.tr`
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
`;

const SupplierProductInfoHeaderCell = styled.th`
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px;
    min-width: 140px;
    font-weight: 800;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-align: left;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;

const SupplierProductInfoHeaderCellSmall = styled(SupplierProductInfoHeaderCell)`
    max-width: 120px;
    min-width: 70px;
    text-align: center;
`;

const ActionsInfoHeaderCellSmall = styled(SupplierProductInfoHeaderCell)`
    min-width: 40px;
    max-width: 40px;
    text-align: center;
`;
